// src/components/settings/GeneralSettings.js
import React, { useState, useContext } from 'react';
import { AppContext } from '../../context/AppContext';
import './SettingsPanel.css';
import { useAuth } from '../../context/AuthContext';
const GeneralSettings = ({ config }) => {
  const { updateConfigSection } = useContext(AppContext);
  const [saving, setSaving] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [status, setStatus] = useState(null);
  const { apiRequest } = useAuth();
  
  // Initialize form state from config
  const [formData, setFormData] = useState({
    name: config.system?.name || 'Mirfa Home',
    notificationsEnabled: config.system?.notificationsEnabled !== false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setStatus(null);
    
    try {
      await updateConfigSection('system', formData);
      setStatus({
        type: 'success',
        message: 'General settings updated successfully.'
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Failed to update general settings.'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRestart = async () => {
    if (!window.confirm('Are you sure you want to restart the hub? This will temporarily disconnect all services.')) {
      return;
    }

    setRestarting(true);
    setStatus(null);

    try {
      const {data} = await apiRequest('/api/system/restart', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (data.success) {
        setStatus({
          type: 'success',
          message: 'Hub restart initiated. The system will be back online shortly.'
        });
        
        // Show loading state for a longer time since the system is restarting
        setTimeout(() => {
          setRestarting(false);
          window.location.reload();
        }, 5000);
      } else {
        throw new Error('Failed to restart hub');
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Failed to restart hub.'
      });
      setRestarting(false);
    }
  };

  return (
    <div className="settings-section">
      <h2>General Settings</h2>
      <p>Configure basic system settings for your smart home hub.</p>
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">System Name</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />
          <div className="form-help">Name used for your smart home system.</div>
        </div>
        
        
        <div className="form-group">
          <input
            type="checkbox"
            id="notificationsEnabled"
            name="notificationsEnabled"
            checked={formData.notificationsEnabled}
            onChange={handleChange}
          />
          <label htmlFor="notificationsEnabled">Enable Notifications</label>
          <div className="form-help">Show system notifications for important events.</div>
        </div>
        
        {status && (
          <div className={`form-status ${status.type}`}>
            {status.message}
          </div>
        )}
        
        <div className="form-buttons">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>

      <div className="settings-section-divider"></div>
      
      <div className="system-controls">
        <h3>System Controls</h3>
        <p>Manage hub system operations.</p>
        
        <div className="form-group">
          <button 
            type="button" 
            className="btn btn-warning" 
            onClick={handleRestart}
            disabled={restarting}
          >
            {restarting ? 'Restarting Hub...' : 'Restart Hub'}
          </button>
          <div className="form-help">
            Restart the hub system. This will temporarily disconnect all services and devices.
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralSettings;